import React, { FC } from 'react';
import ChevronIcon from '../icons/Chevron';
import DoubleChevronIcon from '../icons/DoubleChevron';
import { Select } from '../Select/Select';
import { twJoin } from '@shared/utils/twMerge';

export const defaultPageSizeOptions = [10, 25, 50, 100];

type PaginationProps = {
  currentPageSize?: number;
  currentPage: number;
  total: number;
  onChange?: (page: number, pageSize: number) => void;
  pageSizeOptions?: number[];
  darkMode?: boolean;
};

export const Pagination: FC<PaginationProps> = ({
  currentPageSize = defaultPageSizeOptions[0],
  currentPage,
  total,
  onChange,
  pageSizeOptions = defaultPageSizeOptions,
  darkMode,
}) => {
  const pages = Math.ceil(total / currentPageSize);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pages;

  let from = currentPage - 2;
  let to = currentPage + 1;

  if (isFirstPage) {
    from = currentPage - 1;
    to = currentPage + 2;
  }
  if (isLastPage) {
    from = currentPage - 3;
    to = currentPage;
  }
  const pagesArray = Array.from(Array(pages).keys()).slice(from, to);

  const handleNextPage = () => {
    if (currentPage < pages) {
      onChange && onChange(currentPage + 1, currentPageSize);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onChange && onChange(currentPage - 1, currentPageSize);
    }
  };

  const handleChangePage = (page: number, pageSize = currentPageSize) => {
    onChange && onChange(page, pageSize);
  };

  const showingFrom = currentPage * currentPageSize - currentPageSize + 1;
  const showingTo = currentPage * currentPageSize;
  return (
    <div
      data-testid="Pagination"
      className={twJoin('flex justify-between pb-2')}
    >
      <div className="text-1216 text-spanishGray flex  items-center">
        <div>View</div>
        <Select
          dark={darkMode}
          onChange={value => {
            handleChangePage(1, value);
          }}
          name="pagination-page-size"
          suffixIcon={
            <ChevronIcon
              dir="down"
              className="h-2 w-2"
            />
          }
          defaultValue={currentPageSize}
          className="!h-4.5 mx-1 w-[7rem] font-medium [&>div]:!rounded-sm"
          options={pageSizeOptions.map(p => ({ label: p, value: p }))}
        />
        <div className="">items per page</div>
      </div>
      <div className="flex items-center">
        <div className="text-1216 text-spanishGray mr-1 ">
          Showing {showingFrom}
          {' – '}
          {showingTo > total ? total : showingTo} of {total}
        </div>
        <button
          onClick={() => {
            handleChangePage(1);
          }}
          className={twJoin(
            'flex h-4 w-4 items-center justify-center rounded-full p-0',
            isFirstPage
              ? 'cursor-default'
              : darkMode
                ? 'hover:bg-pageBackgroundDark cursor-pointer'
                : 'hover:bg-waterLight cursor-pointer'
          )}
        >
          <DoubleChevronIcon
            dir="left"
            className={twJoin(
              'h-4 w-4',
              isFirstPage || darkMode ? 'text-americanSilver' : 'text-chineseBlack'
            )}
          />
        </button>
        <button
          onClick={handlePrevPage}
          className={twJoin(
            'flex h-4 w-4 items-center justify-center rounded-full',
            isFirstPage
              ? 'cursor-default'
              : darkMode
                ? 'hover:bg-pageBackgroundDark cursor-pointer'
                : 'hover:bg-waterLight cursor-pointer'
          )}
        >
          <ChevronIcon
            dir="left"
            className={twJoin(
              'h-2 w-2',
              isFirstPage || darkMode ? 'text-americanSilver' : 'text-chineseBlack'
            )}
          />
        </button>
        {pagesArray.map(page => {
          const isActive = page + 1 === currentPage;
          const isHoverable = !isActive;
          return (
            <button
              key={page}
              onClick={() => handleChangePage(page + 1)}
              className={twJoin(
                'mx-[.2rem] flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-lg font-medium leading-8',
                page + 1 === currentPage &&
                  (darkMode ? 'bg-waterLight20 text-white' : 'bg-dodgerBlue text-white'),
                darkMode && 'text-white',
                isHoverable && (darkMode ? 'hover:bg-pageBackgroundDark' : 'hover:bg-waterLight')
              )}
            >
              {page + 1}
            </button>
          );
        })}
        <button
          onClick={handleNextPage}
          className={twJoin(
            'flex h-4 w-4 items-center justify-center rounded-full',
            isLastPage
              ? 'cursor-default'
              : darkMode
                ? 'hover:bg-pageBackgroundDark cursor-pointer'
                : 'hover:bg-waterLight cursor-pointer'
          )}
        >
          <ChevronIcon
            dir="right"
            className={twJoin(
              'h-2 w-2',
              isLastPage || darkMode ? 'text-americanSilver' : 'text-chineseBlack'
            )}
          />
        </button>
        <button
          onClick={() => handleChangePage(pages)}
          className={twJoin(
            'flex h-4 w-4 items-center justify-center rounded-full',
            isLastPage
              ? 'cursor-default'
              : darkMode
                ? 'hover:bg-pageBackgroundDark cursor-pointer'
                : 'hover:bg-waterLight cursor-pointer'
          )}
        >
          <DoubleChevronIcon
            dir="right"
            className={twJoin(
              'h-4 w-4',
              isLastPage || darkMode ? 'text-americanSilver' : 'text-chineseBlack'
            )}
          />
        </button>
      </div>
    </div>
  );
};
